import React, { useState, useEffect } from 'react';
import './VisitModal.css';
import config from './config';

const VisitModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [animateIn, setAnimateIn] = useState(false);

  
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    const fourWeeksFromNow = new Date();
    fourWeeksFromNow.setDate(today.getDate() + 28); 

    let currentDate = new Date(today);
    while (currentDate <= fourWeeksFromNow) {
      const day = currentDate.getDay();
      if (day === 5 || day === 6 || day === 0) { 
        dates.push({
          value: currentDate.toISOString().split('T')[0],
          label: currentDate.toLocaleDateString('en-US', { 
            weekday: 'long',
            month: 'long',
            day: 'numeric'
          })
        });
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    setAvailableDates(dates);
  };

  useEffect(() => {
   
    generateAvailableDates();

    
    const intervalId = setInterval(() => {
      generateAvailableDates();
    }, 24 * 60 * 60 * 1000); 

    
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (isOpen) {
      
      setCurrentStep(1);
      setAnimateIn(true);
      document.body.style.overflow = 'hidden'; 
    } else {
      setAnimateIn(false);
      document.body.style.overflow = 'auto'; 
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    
    if (name === 'phone') {
      
      const sanitizedValue = value.replace(/\D/g, '').substring(0, 11);
      setFormData(prev => ({
        ...prev,
        [name]: sanitizedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

 
  const isValidPhoneNumber = (phone) => {
    const phonePattern = /^[0-9]{11}$/;
    return phonePattern.test(phone);
  };
  
  const isValidEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const nextStep = () => {
    setAnimateIn(false);
    setTimeout(() => {
      setCurrentStep(prev => prev + 1);
      setAnimateIn(true);
    }, 300);
  };

  const prevStep = () => {
    setAnimateIn(false);
    setTimeout(() => {
      setCurrentStep(prev => prev - 1);
      setAnimateIn(true);
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${config.apiBaseUrl}/plan-visit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        date: '',
        message: ''
      });
      
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Failed to send message. Please try again later.');
    }
  };

  const handleCloseModal = () => {
    setAnimateIn(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!isOpen) return null;

  const validateStep = () => {
    if (currentStep === 1) {
      return formData.name.trim() !== '';
    } else if (currentStep === 2) {
      const validEmail = isValidEmail(formData.email);
      const validPhone = isValidPhoneNumber(formData.phone);
      return validEmail && validPhone;
    }
    return true;
  };

  // Add a function to prevent form submission on Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && currentStep !== 3) {
      e.preventDefault();
      if (validateStep()) {
        nextStep();
      }
    }
  };

  return (
    <div className={`modal-overlay ${animateIn ? 'show' : ''}`} onClick={handleCloseModal}>
      <div className={`modal-content ${animateIn ? 'slide-in' : ''}`} onClick={e => e.stopPropagation()}>
        <button className="close-button" onClick={handleCloseModal}>×</button>
        
        {isSubmitted ? (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Thank You!</h2>
            <p>We're excited to welcome you to Faith Tabernacle Church. Our welcome team will be in touch with you shortly to confirm your visit.</p>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <h2>Plan Your Visit</h2>
              <div className="steps-indicator">
                {[1, 2, 3].map(step => (
                  <div 
                    key={step} 
                    className={`step ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}
                  >
                    {step}
                  </div>
                ))}
              </div>
            </div>
            
            <form className="visit-form" onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
              <div className={`form-step ${currentStep === 1 ? 'active' : ''}`}>
                {currentStep === 1 && (
                  <div className="step-content">
                    <div className="welcome-message">
                      <h3>We're excited to meet you!</h3>
                      <p>Let us know who you are, and we'll make sure you have a blessed first experience with us.</p>
                      <div className="quote-container">
                        <p className="bible-verse">"For where two or three gather in my name, there am I with them."</p>
                        <p className="verse-reference">- Matthew 18:20</p>
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="name">What's your name?</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        required
                        autoFocus
                      />
                    </div>
                    <div className="form-navigation">
                      <button 
                        type="button" 
                        className="next-button" 
                        onClick={nextStep} 
                        disabled={!validateStep()}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className={`form-step ${currentStep === 2 ? 'active' : ''}`}>
                {currentStep === 2 && (
                  <div className="step-content">
                    <div className="welcome-message">
                      <h3>How can we reach you?</h3>
                      <p>We'll send you a confirmation and helpful information about your visit.</p>
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your.email@example.com"
                        required
                      />
                      {currentStep === 2 && formData.email && !isValidEmail(formData.email) && (
                        <div className="validation-error">Please enter a valid email address</div>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="07123456789"
                        required
                        maxLength="11"
                        pattern="[0-9]{11}"
                      />
                      {currentStep === 2 && formData.phone && !isValidPhoneNumber(formData.phone) && (
                        <div className="validation-error">Please enter a valid 11-digit phone number</div>
                      )}
                    </div>
                    <div className="form-navigation">
                      <button type="button" className="back-button" onClick={prevStep}>
                        Back
                      </button>
                      <button 
                        type="button" 
                        className="next-button" 
                        onClick={nextStep}
                        disabled={!validateStep()}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className={`form-step ${currentStep === 3 ? 'active' : ''}`}>
                {currentStep === 3 && (
                  <div className="step-content">
                    <div className="welcome-message">
                      <h3>When would you like to visit?</h3>
                      <p>Select your preferred date and let us know of any special requirements.</p>
                    </div>
                    <div className="form-group">
                      <label htmlFor="date">Preferred Visit Date</label>
                      <select
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        className="date-select"
                      >
                        <option value="">Select a date</option>
                        {availableDates.map(date => (
                          <option key={date.value} value={date.value}>
                            {date.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="message">Is there anything else we should know?</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Any accessibility needs, kids joining, or questions you have"
                        rows="3"
                      />
                    </div>
                    <div className="form-navigation">
                      <button type="button" className="back-button" onClick={prevStep}>
                        Back
                      </button>
                      <button type="submit" className="submit-button">
                        Confirm Visit
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default VisitModal; 